import { resetInvoicePdfWork } from '../features/invoices/invoicePdfUpload';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { currentUserEmail, redirectToHostedLogout, signOut as cognitoSignOut } from './cognito';
import { AuthContext, type AuthState } from './context';
import { DemoAuthProvider } from '../demo/DemoAuthProvider';
import { isDemo } from '../demo/flag';

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isDemo()) return <DemoAuthProvider>{children}</DemoAuthProvider>;
  return <CognitoAuthProvider>{children}</CognitoAuthProvider>;
}

function CognitoAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const sessionEmail = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    const e = await currentUserEmail();
    if (sessionEmail.current !== e) {
      resetInvoicePdfWork();
      await queryClient.cancelQueries();
      queryClient.clear();
      sessionEmail.current = e;
    }
    setEmail(e);
    setStatus(e ? 'signedIn' : 'signedOut');
  }, [queryClient]);

  useEffect(() => {
    // Silent re-auth on page load via the persisted refresh token
    void refresh();
  }, [refresh]);

  const signOut = () => {
    resetInvoicePdfWork();
    cognitoSignOut();
    void queryClient.cancelQueries();
    queryClient.clear();
    sessionEmail.current = null;
    setEmail(null);
    setStatus('signedOut');
    // End the hosted-UI (Cognito / Google SSO) session too, so the next
    // "Sign in" really asks who you are.
    redirectToHostedLogout();
  };

  return (
    <AuthContext.Provider value={{ status, email, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
